import { TZDate } from '@date-fns/tz';
import {
  addDays,
  addWeeks,
  addMonths,
  addYears,
  addQuarters,
  addHours,
  addMinutes,
  addSeconds,
  startOfDay,
  endOfDay,
  startOfISOWeek,
  endOfISOWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  startOfQuarter,
  endOfQuarter,
  startOfHour,
  endOfHour,
  startOfMinute,
  endOfMinute,
  startOfSecond,
  endOfSecond,
  format
} from 'date-fns';

type DateRange = [string, string];
type TimeUnit = 'day' | 'week' | 'month' | 'year' | 'quarter' | 'hour' | 'minute' | 'second';

const DATETIME_FORMAT = "yyyy-MM-dd'T'HH:mm:ss.SSS";

const SINGLE_UNIT_RE = /^(this|last|next)\s+(day|week|month|year|quarter|hour|minute|second)$/;
const MULTI_UNIT_RE = /^(last|next)\s+(\d+)\s+(day|week|month|year|quarter|hour|minute|second)s?$/;
const FROM_TO_RE = /^from\s+(.+)\s+to\s+(.+)$/;
const AGO_RE = /^(\d+)\s+(day|week|month|year|quarter|hour|minute|second)s?\s+ago$/;
const FROM_NOW_RE = /^(\d+)\s+(day|week|month|year|quarter|hour|minute|second)s?\s+from\s+now$/;

export function parseDateRange(
  dateString: string,
  timezone: string,
  now: Date = new Date()
): DateRange {
  const input = dateString.toLowerCase().trim();
  const tzNow = new TZDate(now, timezone);

  if (input === 'today') {
    return [formatDT(startOfDay(tzNow)), formatDT(endOfDay(tzNow))];
  }

  if (input === 'yesterday') {
    const d = addDays(tzNow, -1);
    return [formatDT(startOfDay(d)), formatDT(endOfDay(d))];
  }

  if (input === 'tomorrow') {
    const d = addDays(tzNow, 1);
    return [formatDT(startOfDay(d)), formatDT(endOfDay(d))];
  }

  const singleMatch = input.match(SINGLE_UNIT_RE);
  if (singleMatch) {
    const [, direction, unitStr] = singleMatch;
    const unit = unitStr as TimeUnit;
    let start: Date = tzNow;
    let end: Date = tzNow;

    if (direction === 'last') {
      start = addUnit(start, -1, unit);
      end = addUnit(end, -1, unit);
    } else if (direction === 'next') {
      start = addUnit(start, 1, unit);
      end = addUnit(end, 1, unit);
    }

    return [formatDT(startOf(start, unit)), formatDT(endOf(end, unit))];
  }

  const multiMatch = input.match(MULTI_UNIT_RE);
  if (multiMatch) {
    const [, direction, countStr, unitStr] = multiMatch;
    const count = parseInt(countStr, 10);
    const unit = unitStr as TimeUnit;
    let start: Date;
    let end: Date;

    if (direction === 'last') {
      start = addUnit(tzNow, -count, unit);
      end = addUnit(tzNow, -1, unit);
    } else {
      start = addUnit(tzNow, 1, unit);
      end = addUnit(tzNow, count, unit);
    }

    return [formatDT(startOf(start, unit)), formatDT(endOf(end, unit))];
  }

  const fromToMatch = input.match(FROM_TO_RE);
  if (fromToMatch) {
    const [, fromStr, toStr] = fromToMatch;
    const fromDate = parseRelativeDate(fromStr.trim(), tzNow);
    const toDate = parseRelativeDate(toStr.trim(), tzNow);

    if (!fromDate) throw new Error(`Can't parse date: '${fromStr.trim()}'`);
    if (!toDate) throw new Error(`Can't parse date: '${toStr.trim()}'`);

    const granularity = detectGranularity(input);
    return [formatDT(startOf(fromDate, granularity)), formatDT(endOf(toDate, granularity))];
  }

  throw new Error(`Can't parse date: '${dateString}'`);
}

function addUnit(date: Date, amount: number, unit: TimeUnit): Date {
  switch (unit) {
    case 'day':
      return addDays(date, amount);
    case 'week':
      return addWeeks(date, amount);
    case 'month':
      return addMonths(date, amount);
    case 'year':
      return addYears(date, amount);
    case 'quarter':
      return addQuarters(date, amount);
    case 'hour':
      return addHours(date, amount);
    case 'minute':
      return addMinutes(date, amount);
    case 'second':
      return addSeconds(date, amount);
  }
}

function startOf(date: Date, unit: TimeUnit): Date {
  switch (unit) {
    case 'day':
      return startOfDay(date);
    case 'week':
      return startOfISOWeek(date);
    case 'month':
      return startOfMonth(date);
    case 'year':
      return startOfYear(date);
    case 'quarter':
      return startOfQuarter(date);
    case 'hour':
      return startOfHour(date);
    case 'minute':
      return startOfMinute(date);
    case 'second':
      return startOfSecond(date);
  }
}

function endOf(date: Date, unit: TimeUnit): Date {
  switch (unit) {
    case 'day':
      return endOfDay(date);
    case 'week':
      return endOfISOWeek(date);
    case 'month':
      return endOfMonth(date);
    case 'year':
      return endOfYear(date);
    case 'quarter':
      return endOfQuarter(date);
    case 'hour':
      return endOfHour(date);
    case 'minute':
      return endOfMinute(date);
    case 'second':
      return endOfSecond(date);
  }
}

function formatDT(date: Date): string {
  return format(date, DATETIME_FORMAT);
}

function detectGranularity(input: string): TimeUnit {
  if (input.includes('second')) return 'second';
  if (input.includes('minute')) return 'minute';
  if (input.includes('hour')) return 'hour';
  return 'day';
}

function parseRelativeDate(str: string, now: Date): Date | null {
  if (str === 'now') return now;

  const agoMatch = str.match(AGO_RE);
  if (agoMatch) {
    return addUnit(now, -parseInt(agoMatch[1], 10), agoMatch[2] as TimeUnit);
  }

  const fromNowMatch = str.match(FROM_NOW_RE);
  if (fromNowMatch) {
    return addUnit(now, parseInt(fromNowMatch[1], 10), fromNowMatch[2] as TimeUnit);
  }

  return null;
}
